/**
 * `<kit-test-context>` — the §4a test seam: mount any element under it with
 * fake services, no app shell required.
 *
 * ```ts
 * defineTestContext();
 * const host = document.createElement("kit-test-context") as KitTestContext;
 * host.provide(SdkToken, fakeSdk).provide(RouterToken, fakeRouter);
 * document.body.append(host);
 * host.append(elementUnderTest); // its inject() now resolves the fakes
 * ```
 *
 * Registration is EXPLICIT via `defineTestContext()` — importing this module
 * has no side effects.
 */

import { createKitContext, type ContextToken, type KitContext } from "../core/context";

/** Lazily-resolved base so importing this module never requires DOM globals. */
const HTMLElementBase: typeof HTMLElement =
  (globalThis as { HTMLElement?: typeof HTMLElement }).HTMLElement ??
  (class {
    constructor() {
      throw new Error(
        "[web-kit] KitTestContext requires a DOM environment (HTMLElement is not defined)."
      );
    }
  } as unknown as typeof HTMLElement);

export class KitTestContext extends HTMLElementBase {
  #ctx: KitContext | null = null;

  /** The context bound to this host (lazily created). */
  get context(): KitContext {
    this.#ctx ??= createKitContext(this);
    return this.#ctx;
  }

  /** Provide a (fake) service for the subtree. Chainable. */
  provide<T>(token: ContextToken<T>, value: T): this {
    this.context.provide(token, value);
    return this;
  }
}

/** Register `<kit-test-context>` (idempotent). Call from test setup. */
export function defineTestContext(): void {
  if (!customElements.get("kit-test-context")) {
    customElements.define("kit-test-context", KitTestContext);
  }
}
